import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, TextField, Grid, MenuItem, Card, CardContent } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import CustomPagination from '../common/CustomPagination';

const InvoiceTable = ({
  invoices,
  loading,
  page,
  rowsPerPage,
  totalPages,
  setPage,
  setRowsPerPage,
  customRowsActive,
  customRowsInput,
  setCustomRowsActive,
  setCustomRowsInput,
  handleCustomRowsInputChange,
  handleCustomRowsInputBlur,
  columns,
  filters,
  onFilterChange,
  clearFilters,
}) => {
  const { t } = useTranslation(['invoiceManagement', 'common']);
  // Pagination handlers
  const handleRowsPerPageChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
      setCustomRowsInput(rowsPerPage.toString());
    } else {
      setRowsPerPage(Number(value));
      setPage(0);
      setCustomRowsActive(false);
    }
  };

  return (
    <>
      {/* Filter Section */}
      <Card sx={{ mb: 3, backgroundColor: 'background.paper' }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                label={t('filters.invoiceNumber')}
                variant="outlined"
                size="small"
                fullWidth
                value={filters.invoiceNumber}
                onChange={(e) => onFilterChange('invoiceNumber', e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                label={t('filters.user')}
                variant="outlined"
                size="small"
                fullWidth
                value={filters.user}
                onChange={(e) => onFilterChange('user', e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                label={t('filters.subject')}
                variant="outlined"
                size="small"
                fullWidth
                value={filters.subject}
                onChange={(e) => onFilterChange('subject', e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                select
                label={t('filters.status')}
                variant="outlined"
                size="small"
                fullWidth
                value={filters.status}
                onChange={(e) => onFilterChange('status', e.target.value)}
              >
                <MenuItem value="">{t('filters.allStatuses')}</MenuItem>
                <MenuItem value="unpaid">{t('status.unpaid')}</MenuItem>
                <MenuItem value="partially_paid">{t('status.partiallyPaid')}</MenuItem>
                <MenuItem value="paid">{t('status.paid')}</MenuItem>
              </TextField>
            </Grid>
          </Grid>
          
          {/* Clear Filters Button */}
          {(filters.invoiceNumber || filters.user || filters.subject || filters.status) && (
            <Box sx={{ mt: 2 }}>
              <Typography
                variant="body2"
                color="primary"
                sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                onClick={clearFilters}
              >
                {t('filters.clearAll')}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* DataGrid */}
      <Card sx={{ backgroundColor: 'background.paper' }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{
            width: '100%',
            overflowX: 'auto',
            minHeight: 520,
            '&::-webkit-scrollbar': {
              height: 8,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0,0,0,0.1)',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.3)',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: 'rgba(0,0,0,0.5)',
            },
          }}>
            <DataGrid
              rows={invoices}
              columns={columns}
              disableSelectionOnClick
              loading={loading}
              hideFooterPagination
              hideFooter
              getRowSpacing={() => ({ top: 2, bottom: 2 })}
              sx={{
                border: 'none',
                minWidth: 1200, // Ensure minimum width for all columns
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: theme => theme.palette.primary.main,
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                },
                '& .MuiDataGrid-columnHeader:focus': {
                  outline: 'none',
                },
                '& .MuiDataGrid-columnHeader:focus-within': {
                  outline: 'none',
                },
                '& .MuiDataGrid-cell:focus': {
                  outline: 'none',
                },
                '& .MuiDataGrid-cell:focus-within': {
                  outline: 'none',
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#FFF0F3',
                },
                '& .MuiDataGrid-row.Mui-selected': {
                  backgroundColor: '#e3f2fd',
                },
                '& .MuiDataGrid-row.Mui-selected:hover': {
                  backgroundColor: '#bbdefb',
                },
                '& .MuiDataGrid-cell': {
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  padding: { xs: '8px 4px', sm: '8px 12px' }
                }
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Custom Pagination */}
      <CustomPagination
        page={page}
        totalPages={totalPages}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={handleRowsPerPageChange}
        customRowsActive={customRowsActive}
        customRowsInput={customRowsInput}
        onCustomRowsInputChange={handleCustomRowsInputChange}
        onCustomRowsInputBlur={handleCustomRowsInputBlur}
      />
    </>
  );
};

export default InvoiceTable;
